/**
 * SbTour - Wrapper around Ant Design Tour
 *
 * Thin passthrough — every AntD Tour prop is supported as-is (steps, open,
 * current, onChange, onClose, onFinish, type, mask, arrow, placement,
 * closeIcon, gap, nextButtonProps/prevButtonProps per step, …). Brand color
 * comes from the theme (colorPrimary) via ConfigProvider — nothing custom.
 *
 * It only sets two sensible defaults, both built from native AntD props:
 *   - indicatorsRender → a "1 / 3" step counter (AntD "Custom indicator")
 *   - actionsRender    → a "Cancel" button before the last step, then AntD's
 *                        own Previous/Next/Finish buttons (AntD "Custom action")
 * Pass your own `indicatorsRender` / `actionsRender` to override either.
 *
 * Each item in `steps` is a standard AntD TourStep:
 *   { target, title, description, placement, cover, nextButtonProps, ... }
 * `target` is typically a function returning a ref element:
 *   target: () => gaugeRef.current
 *
 * <SbTour open={open} steps={steps} onClose={() => setOpen(false)} />
 */

import React from 'react';
import { Tour } from 'antd';
import SbButton, { BUTTON_TYPE } from '../SbButton/SbButton';

const SbTour = ({
  steps = [],
  open = false,
  onClose,
  cancelText = 'Cancel',
  indicatorsRender,
  actionsRender,
  ...rest
}) => {
  // Default step counter "1 / 3" (AntD indicatorsRender). Overridable.
  const mergedIndicatorsRender =
    indicatorsRender ?? ((current, total) => `${current + 1} / ${total}`);

  // Default actions: a Cancel button (hidden on the last step) followed by
  // AntD's own Previous/Next/Finish buttons (originNode). Overridable.
  const mergedActionsRender =
    actionsRender ??
    ((originNode, { current, total }) => (
      <>
        {current !== total - 1 && (
          <SbButton buttonType={BUTTON_TYPE.SECONDARY} size="small" onClick={onClose}>
            {cancelText}
          </SbButton>
        )}
        {originNode}
      </>
    ));

  return (
    <Tour
      steps={steps}
      open={open}
      onClose={onClose}
      indicatorsRender={mergedIndicatorsRender}
      actionsRender={mergedActionsRender}
      {...rest}
    />
  );
};

export default SbTour;
